import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, query, getDocs, where } from 'firebase/firestore';
import Footer from '../components/Footer';

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [nextProject, setNextProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lightbox state
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setIsLoading(true);
        // Fetch the single portfolio document
        const docRef = doc(db, 'portfolios', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const portfolioData = { id: docSnap.id, ...docSnap.data() };
          setPortfolio(portfolioData);

          // Set the document title
          document.title = `Fere | ${portfolioData.title}`;

          // Fetch all portfolios to find the next one (client-side sorting to avoid index issue)
          const portfoliosRef = collection(db, 'portfolios');
          const q = query(portfoliosRef, where('is_published', '==', true));
          const allDocsSnap = await getDocs(q);
          let allPortfolios = allDocsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          
          allPortfolios.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          
          const currentIndex = allPortfolios.findIndex(p => p.id === id);
          if (currentIndex !== -1) {
            const nextIndex = (currentIndex + 1) % allPortfolios.length;
            setNextProject(allPortfolios[nextIndex]);
          }

        } else {
          setError('포트폴리오를 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('Error fetching portfolio:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchPortfolio();
    }
  }, [id]);

  useEffect(() => {
    if (!isLoading) {
      document.body.setAttribute('data-barba-namespace', 'project-detail');
      // Potentially re-initialize some scripts if needed
    }
    return () => {
      document.body.removeAttribute('data-barba-namespace');
    }
  }, [isLoading]);


  if (isLoading) {
    return <div className="text-center"></div>;
  }

  if (error) {
    return <div className="text-center p-12 text-red-500">오류: {error}</div>;
  }

  if (!portfolio) {
    return <div className="text-center p-12">포트폴리오를 찾을 수 없습니다.</div>;
  }
  
  const slides = portfolio.images ? portfolio.images.map(url => ({ src: url })) : [];

  return (
    <>
      <main className="page_main">
        <section data-inverse-translate="0" data-overlay-container="" data-target-opacity="0.3" data-scroll-container="" className="hero_project_wrap">
          <div className="hero_project_cover">
            <div data-overlay-scroll="" className="hero_home_overlay" style={{opacity: 0}}></div>
            <div className="hero_project_overlay"></div>
            <img src={portfolio.thumbnail_url} loading="eager" data-translate-hero="" alt={portfolio.title} className="hero_project_thumbnail" />
          </div>
          <div className="hero_project_hero u-grid-custom">
            <h1 aria-hidden={true} data-lines-split="" data-split-animation="" id="w-node-d97d31c0-51b5-f99c-3ac9-fcfd12643943-36330cd0" className="hero_project_heading u-text-display">{portfolio.title}</h1>    
            <div className="hero_project_bottom u-grid-custom">
              <div id="w-node-_373b4526-c2fd-b37d-7051-322c35c6281d-36330cd0" className="hero_project_contain is-location">
                <div data-details-animation="" id="w-node-_646db56b-f12a-5e6d-5623-bd420cf1f76e-36330cd0" className="hero_project_location u-text-small">{portfolio.location}</div>
              </div>
              
            </div>
          </div>
          <div className="hero_project_content u-grid-tablet">
            <div id="w-node-f8b8d5a0-5429-2c40-d053-154b8741e84e-36330cd0" className="project_content_wrap">
              <div className="project_content_paragraph u-text-large w-richtext">
                <p>{portfolio.description}</p>
              </div>
              <div className="project_content_details u-grid-custom">
                <div id="w-node-_8981e9ec-5fb4-78cc-af83-48e713b85048-36330cd0" className="project_details_wrap">
                  <div className="project_details_heading u-text-main">완료일</div>
                  <div className="project_details_text u-text-small">{new Date(portfolio.created_at).getFullYear()}</div>
                </div>
                <div id="w-node-_621714cf-371c-12b1-c56c-14ddac6fa8af-36330cd0" className="project_details_wrap">
                  <div className="project_details_heading u-text-main">프로젝트 타입</div>
                  <div className="project_details_text u-text-small">{portfolio.style}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="projects_gallery_wrap">
          <div className="projects_gallery_collection w-dyn-list">
            <div role="list" className="projects_gallery_list u-grid-tablet w-dyn-items">
              {portfolio.images && portfolio.images.map((image, idx) => (
                <div 
                  role="listitem" 
                  data-image-orientation="landscape" 
                  className="projects_gallery_item w-dyn-item cursor-pointer" 
                  key={idx}
                  onClick={() => {
                    setIndex(idx);
                    setOpen(true);
                  }}
                >
                  <div data-image-reveal="" className="projects_gallery_cover">
                    <div data-image-cover="" className="g_image_cover"></div>
                    <img src={image} loading="lazy" data-image="" alt={`${portfolio.title} gallery ${idx + 1}`} className="projects_gallery_image" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {nextProject && (
          <section className="projects_next_wrap">
            <div className="projects_next_deco">
              <div className="g_plus u-text-h3">+</div>
              <div className="projects_next_text u-text-small">(next project)</div>
              <div className="g_plus u-text-h3">+</div>
            </div>
            <div className="projects_next_project">
              <h2 className="projects_next_heading u-text-display">{nextProject.title}</h2>
              <Link data-hover-container="" to={`/project/${nextProject.id}`} className="projects_next_link w-inline-block">
                <div data-image-reveal="" className="projects_next_cover">
                  <div data-image-cover="" className="g_image_cover"></div>
                  <div data-hover-image="" className="projects_image_contain">
                    <img src={nextProject.thumbnail_url} loading="lazy" data-image="" alt={nextProject.title} className="projects_image_next" />
                  </div>
                </div>
              </Link>
            </div>
            <div className="projects_next_deco">
              <div className="g_plus u-text-h3">+</div>
              <div className="g_plus u-text-h3">+</div>
            </div>
          </section>
        )}
         <Footer />
      </main>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
      />
    </>
  );
};

export default ProjectDetailPage;
